/* Hauser MX — El Recorrido: el escenario de la ficha (Fase 15).
 *
 * ── El problema que resuelve ──────────────────────────────────────────
 * La ficha es una rejilla de dos columnas: izquierda la galería, derecha
 * TODO lo demás (título, precio, specs, destacados, descripción, amenidades,
 * mapa, similares). La galería mide una pantalla; la columna de información
 * mide seis. Desde "Lo que más nos gusta" hacia abajo, la mitad izquierda de
 * la pantalla estaba VACÍA durante el 80% de la página.
 *
 * Y eso es lo caro: el visitante lee "recámara principal con balcón y baño
 * con tina" mientras la foto de esa recámara ya desapareció. En una
 * inmobiliaria las fotos SON el producto, y las apagábamos justo cuando
 * empieza el deseo.
 *
 * (La galería sí intentaba quedarse anclada, pero nunca lo consiguió: en
 * hauser-ficha.css la regla `position: sticky` del @media se declara ANTES
 * que un `position: relative` de la misma especificidad, así que la cascada
 * se la comía en silencio. Arreglado en hauser-escena.css.)
 *
 * ── Qué hace ──────────────────────────────────────────────────────────
 * La columna izquierda pasa a ser un escenario anclado con tres paneles que
 * se relevan según DÓNDE va leyendo el visitante:
 *
 *   1. FOTOS   — las fotos de la casa avanzan con el scroll. Nunca lees
 *                sobre una habitación que no puedes ver.
 *   2. NÚMEROS — al llegar a amenidades, el escenario cambia a las cifras.
 *   3. CITA    — al llegar al mapa, se convierte en el panel de cierre y se
 *                queda ahí. Media pantalla dedicada a conseguir la visita en
 *                el momento de máximo interés.
 *
 * ── Por qué las fotos NO se duplican en el HTML ───────────────────────
 * El panel de fotos se construye clonando las <img> que la galería ya
 * imprimió. Repetir 24 imágenes en el marcado habría duplicado el peso del
 * documento para enseñar exactamente lo mismo, y habría creado dos listas
 * que se desincronizan en cuanto alguien reordena las fotos en Shopify. Las
 * URL son las mismas, así que el navegador las sirve de su caché.
 *
 * Consecuencia deliberada: sin JS no hay panel de fotos. No importa — los
 * paneles de números y cita son HTML plano y se ven igual, y la galería de
 * arriba sigue enseñando la casa entera.
 *
 * ── El orden de las fotos ES el orden del recorrido ───────────────────
 * No se intenta adivinar qué foto es "planta alta": no hay ningún dato fiable
 * para eso (los alt text suelen venir vacíos). Se reparten las fotos a lo
 * largo del scroll en su orden real. Como el dueño ya sube las fotos en orden
 * de visita —fachada, planta baja, planta alta, amenidades— y la descripción
 * está escrita en ese mismo orden, la sincronía sale sola y sin capturar ni
 * un dato extra. Si reordena las fotos en Shopify, reordena el recorrido.
 *
 * ── Por qué el relevo NO usa IntersectionObserver ─────────────────────
 * Las anclas miden 1px. Con un observer hay que darle una franja estrecha
 * para que el panel no cambie demasiado pronto, y un scroll rápido puede
 * atravesar esa franja entre dos cálculos sin que el observer dispare: el
 * escenario se quedaría en el panel anterior hasta la siguiente ancla. Aquí
 * el relevo se decide midiendo la posición de TODAS las anclas en el mismo
 * bucle de scroll que ya mueve las fotos: es imposible saltarse un paso,
 * porque no depende de un evento sino del estado.
 *
 * ── Escritorio y nada más ─────────────────────────────────────────────
 * En móvil no hay hueco que llenar: una sola columna. El guion se retira
 * antes de hacer nada y el CSS esconde el escenario. Cero trabajo en el
 * dispositivo donde más se ve el sitio.
 */
(function () {
  'use strict';

  var ANCHO = window.matchMedia('(min-width: 990px)');
  var MENOS_MOVIMIENTO = window.matchMedia('(prefers-reduced-motion: reduce)');

  function iniciar() {
    var escena = document.querySelector('[data-hauser-escena]');
    if (!escena) return;
    if (escena.dataset.escenaListo === '1') return;

    /* En móvil el escenario no existe: ni se monta, ni se piden sus fotos.
       No se marca como listo, para que se monte si la ventana crece. */
    if (!ANCHO.matches) return;
    escena.dataset.escenaListo = '1';

    var info = document.querySelector('.hauser-ficha__info');
    var panelFotos = escena.querySelector('[data-escena-panel="fotos"]');
    var marco = escena.querySelector('[data-escena-marco]');
    var elActual = escena.querySelector('[data-escena-actual]');
    var elTotal = escena.querySelector('[data-escena-total]');
    var elPie = escena.querySelector('[data-escena-pie]');
    var relleno = escena.querySelector('[data-escena-relleno]');
    var btnAmpliar = escena.querySelector('[data-escena-ampliar]');

    /* ── Todo el estado, declarado ANTES de que nada lo use ───────────
       No es manía de orden. En la primera versión `poner(0, true)` se
       llamaba unas líneas por encima de `var frente = 0`: por el izado de
       `var`, en esa llamada `frente` valía `undefined`, `lienzos[1 - frente]`
       daba `lienzos[NaN]` —o sea `undefined`— y asignarle `.src` lanzaba una
       excepción que se llevaba por delante el escenario ENTERO, incluidos el
       scroll y el relevo de paneles. No daba error de sintaxis; solo no
       funcionaba nada. */
    var fuentes = [];
    var lienzos = [];
    var indice = -1;
    /* Arranca en 1 para que la PRIMERA foto entre en lienzos[0], que es el que
       se crea con `loading="eager"`. */
    var frente = 1;
    var actual = null;
    var pendiente = false;

    /* ── Las fotos, tomadas de la galería que ya está en la página ───── */

    var capas = document.querySelectorAll('[data-hauser-galeria-capa]');

    Array.prototype.forEach.call(capas, function (capa) {
      /* Solo imágenes: un <video> dentro del escenario se reproduciría sin que
         nadie lo haya pedido, y además no se puede "cruzar" con una foto. */
      if (capa.getAttribute('data-tipo') !== 'image') return;
      var img = capa.querySelector('img');
      if (!img) return;
      fuentes.push({
        src: img.getAttribute('src') || '',
        srcset: img.getAttribute('srcset') || '',
        alt: img.getAttribute('alt') || ''
      });
    });

    /* Con menos de dos fotos no hay recorrido que enseñar: el panel se retira
       y el escenario arranca directamente en los números. */
    if (fuentes.length < 2 && panelFotos) {
      panelFotos.remove();
      panelFotos = null;
      marco = null;
    }

    if (elTotal) elTotal.textContent = ('0' + fuentes.length).slice(-2);

    function poner(n, inmediato) {
      if (!lienzos.length) return;
      n = Math.max(0, Math.min(fuentes.length - 1, n));
      if (n === indice) return;
      indice = n;

      var dato = fuentes[n];
      var entra = lienzos[1 - frente];
      var sale = lienzos[frente];
      if (!entra || !sale) return;

      entra.src = dato.src;
      if (dato.srcset) entra.setAttribute('srcset', dato.srcset);
      entra.sizes = '46vw';

      /* La que entra se pinta encima; la que sale se apaga debajo. Sin el
         z-index alterno, la disolvencia deja ver el fondo a mitad de camino
         y parece un parpadeo. */
      entra.style.zIndex = '2';
      sale.style.zIndex = '1';
      entra.classList.add('is-visible');
      if (!inmediato) sale.classList.remove('is-visible');

      frente = 1 - frente;

      if (elActual) elActual.textContent = ('0' + (n + 1)).slice(-2);
      /* El alt de la foto, cuando el dueño lo capturó, dice de qué habitación
         se trata. Cuando no, no se inventa nada: el pie se queda vacío. */
      if (elPie) {
        var texto = (dato.alt || '').trim();
        var generico = texto === '' || texto.indexOf(' — foto ') !== -1;
        elPie.textContent = generico ? '' : texto;
        elPie.hidden = generico;
      }
    }

    /* Dos capas que se turnan. Con una sola no hay disolvencia posible, y con
       una por foto habría 24 elementos compuestos a la vez: dos bastan para
       cruzar de cualquier foto a cualquier otra. */
    if (marco && fuentes.length) {
      for (var i = 0; i < 2; i++) {
        var lienzo = document.createElement('img');
        lienzo.className = 'hauser-escena__img';
        lienzo.decoding = 'async';
        lienzo.alt = '';
        /* La primera se pide ya; la segunda espera a que haga falta. */
        lienzo.loading = i === 0 ? 'eager' : 'lazy';
        marco.appendChild(lienzo);
        lienzos.push(lienzo);
      }
      poner(0, true);
    }

    /* ── Los paneles ──────────────────────────────────────────────────── */

    var paneles = {};
    Array.prototype.forEach.call(
      escena.querySelectorAll('[data-escena-panel]'),
      function (p) { paneles[p.getAttribute('data-escena-panel')] = p; }
    );

    /* Las anclas son elementos vacíos colocados en la columna de lectura, no
       las secciones mismas: una sección puede no existir (una propiedad sin
       amenidades) y entonces el panel no se relevaría nunca. Las anclas
       siempre están. */
    var anclas = Array.prototype.slice.call(
      document.querySelectorAll('[data-hauser-escena-ancla]')
    );

    var inicial = 'cita';
    if (paneles.fotos && panelFotos) inicial = 'fotos';
    else if (paneles.numeros) inicial = 'numeros';

    function mostrar(nombre) {
      if (!paneles[nombre] || nombre === actual) return;
      actual = nombre;
      Object.keys(paneles).forEach(function (k) {
        paneles[k].classList.toggle('is-activo', k === nombre);
        paneles[k].setAttribute('aria-hidden', k === nombre ? 'false' : 'true');
      });
      escena.setAttribute('data-escena-acto', nombre);
    }

    mostrar(inicial);

    /* ── El progreso de lectura y el relevo, en el mismo bucle ────────── */

    /* Horizon cambia de contenedor de scroll a los 990px: en escritorio
       scrollea `.page-wrapper` y no la ventana. Escuchar solo `window` dejaría
       el escenario congelado justo en el ancho en el que existe. */
    var H = window.Hauser;
    var contenedor = (H && H.scroller) || document.querySelector('.page-wrapper');

    function alScroll() {
      if (pendiente) return;
      pendiente = true;
      window.requestAnimationFrame(medir);
    }

    function medir() {
      pendiente = false;
      var alto = window.innerHeight || document.documentElement.clientHeight;

      /* Relevo de paneles: gana la ÚLTIMA ancla que ya cruzó la línea del 40%
         de la pantalla. Al ser una decisión sobre el estado y no sobre un
         evento, da igual lo rápido que se scrollee: nunca se salta un paso, y
         al subir vuelve sola al panel anterior. */
      var linea = alto * 0.4;
      var toca = inicial;
      for (var k = 0; k < anclas.length; k++) {
        if (anclas[k].getBoundingClientRect().top <= linea) {
          toca = anclas[k].getAttribute('data-hauser-escena-ancla');
        }
      }
      mostrar(toca);

      if (!info) return;
      var caja = info.getBoundingClientRect();

      /* Progreso de la COLUMNA DE LECTURA, no de la página: lo que gobierna el
         recorrido es cuánto has leído, no cuánto has bajado. */
      var recorrido = caja.height - alto;
      var p = recorrido > 0 ? (-caja.top) / recorrido : 0;
      p = Math.max(0, Math.min(1, p));

      if (relleno) relleno.style.transform = 'scaleY(' + p.toFixed(4) + ')';

      if (fuentes.length > 1) {
        /* El último tramo del scroll ya no es recorrido (ahí mandan los
           paneles de números y cita), así que las fotos se reparten en el
           primer 78% para que la última foto no llegue con la página acabada. */
        var pf = Math.min(1, p / 0.78);
        poner(Math.round(pf * (fuentes.length - 1)));
      }
    }

    window.addEventListener('scroll', alScroll, { passive: true });
    if (contenedor) contenedor.addEventListener('scroll', alScroll, { passive: true });
    window.addEventListener('resize', alScroll, { passive: true });
    medir();

    /* ── Ampliar: se reutiliza el visor que ya existe ─────────────────── */

    /* No se construye una segunda pantalla completa. El botón le pasa el clic
       al de la galería, que ya trae su <dialog> nativo con foco atrapado y
       cierre con Escape. Dos lightbox en la misma página sería un error caro
       de mantener. */
    if (btnAmpliar) {
      var origen = document.querySelector('[data-hauser-galeria-expandir]');
      if (origen) {
        btnAmpliar.addEventListener('click', function () { origen.click(); });
      } else {
        btnAmpliar.hidden = true;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }

  /* Si se agranda la ventana cruzando los 990px, el escenario se monta. Al
     revés no se desmonta: el CSS ya lo esconde y desmontarlo obligaría a
     deshacer listeners por una situación que casi nadie provoca. */
  if (ANCHO.addEventListener) {
    ANCHO.addEventListener('change', function (e) { if (e.matches) iniciar(); });
  }

  document.addEventListener('shopify:section:load', iniciar);
})();